// React
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

// Third-party libraries
import { FormattedMessage, useIntl } from "react-intl";
import "react-phone-input-2/lib/material.css";

// Material-UI
import {
  Button,
  Grid,
  IconButton,
  LinearProgress,
  MuiThemeProvider,
  Popover,
  Tooltip,
  Typography,
  createMuiTheme,
  makeStyles,
} from "@material-ui/core";
import { ArrowBack, DoubleArrow, InfoOutlined } from "@material-ui/icons";

// Custom components
import CustomPhoneInput from "../../../components/FormComponents/PhoneInput";
import CustomSelect from "../../../components/FormComponents/Select";
import DatePicker from "../../../components/FormComponents/DatePicker";
import AddressInput from "../../../components/AddressInput";
import TextInput from "../../../components/FormComponents/TextInput";
import ErrorBoundary from "../../../components/ErrorBoundary";

// Utils and constants
import { classes } from "../../../utils/styledClasses";
import { STEPS, customStyles } from "../../../utils/constants";
import { registrationStyle } from "../../../utils/StaticRegistration/registrationStyle";

// Services
import getFile from "../../../../services/sharedService";
import {
  getTaxesObject,
  getTaxesPayer,
} from "../../../../services/taxes.service";
import { getCities } from "services/Cities";

// Shared constants
import { city } from "../../../../shared/constant";

// Assets
import bg from "../../../images/bg-logo.jpg";

const theme = createMuiTheme({
  palette: {
    secondary: {
      main: "rgb(100, 38, 187)",
    },
  },
});
const useStyle = makeStyles({
  inputStyle: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#215CA8",
      },
    },
    "& .MuiSelect-select": {
      padding: "10.5px 14px",
    },
  },
  phoneContainer: {
    width: "100%",
  },
  label: {},
  button: {
    display: "block",
    marginTop: theme.spacing(2),
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  logo: {
    width: "200px",
  },
  uploadImage: {
    position: "absolute",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
    background: "#00000086",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    opacity: 0,
    transition: "300ms",

    "&:hover": {
      opacity: 1,
    },

    "& svg": {
      color: "#fff",
      width: "30px",
      height: "30px",
    },
  },
});

let newPhoto = {};

const textPopover =
  "Bələdçilərin fotoşəkilləri özlərinə xas olmalıdır.  Fotoşəkillər bələdçilərin mehriban və öz işinin peşəkarı olduğunu əks etdirməlidir. Zəhmət olmasa minimum 3 şəkil təqdim edin. \nŞəkillərin yüksək xüsusiyyətə malik olması vacibdir: \nRəng xüsusiyyəti – RGB \nŞəkil formatı – JPG, PNG \nŞəkil ölçüləri – 1024 x 968 px-dən kiçik olmayan \nŞəklin ölçüsü – maks. 2mb \nAşağıdakılardan yayının \n● Qırmızı və ya  pikselli fotoşəkillər \n● Bulanıq və ya əyilmiş fotoşəkillər \n● Həddindən artıq işlənmiş fotoşəkillər \n● Filtrlər, güclü kontrast və ya həddindən artıq HDR olan fotoşəkillər \n● Kolajlar, selfilər \n● Fotolarda su nişanları və ya illüstrasiyalar \n● Əyilmiş, uzadılmış və ya təhrif olunmuş fotoşəkillər";

const StaticCriteriaQuestions = ({
  person,
  isInstitution,
  stakeholder,
  formik,
  disabled,
  selectedIndustryCategory,
  voenInput,
  setRegistrationStep,
  registerAppEdit,
  handleSubmit,
}) => {
  console.log("[STATIC CRITERIA DEBUG] Component initialized", {
    personName: person?.name,
    isInstitution,
    stakeholderVoen: stakeholder?.voen,
    selectedIndustryCategory: selectedIndustryCategory?.name,
    disabled,
    hasFormik: !!formik,
    registerAppEdit,
  });
  const history = useHistory();
  const intl = useIntl();
  const classMaterial = useStyle();
  const [googleMapAddress, setGoogleMapAddress] = React.useState({});
  const [formikPhoto, setFormikPhoto] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [taxPayerObjData, setTaxPayerObjData] = React.useState([]);
  const [taxPayerInfoLoading, setTaxPayerInfoLoading] = useState(false);
  const [anchorElPopover, setAnchorElPopover] = useState(null);
  const [cities, setCities] = useState(city);

  // This page is also accessible by this path (i.e.not only on registaation): /stakeholder/cabinet/profile-view/edit
  // so developer can edit and run this page locally instead of deploy it after every edit to see changes

  if (process.env.REACT_APP_ENV === "development") {
    // FAKE DATA FOR DEVELOPMENT, SHOWS ALL AVAILABEL INPUTS
    selectedIndustryCategory = {
      name: {
        az: "Bələdçilər",
        // az: "Yerləşmə vasitələri",
      },
    };
    // isInstitution = false;
    stakeholder.voen = "88745983";
  }

  const openPopover = Boolean(anchorElPopover);
  const idPopover = openPopover ? "simple-popover" : undefined;

  useEffect(() => {
    getData();
    fetchCities();
  }, []);

  const getData = async () => {
    console.log("[STATIC CRITERIA DEBUG] getData: Starting...");
    const selectedVoen = localStorage.getItem("selectedVoen");
    const localData = localStorage.getItem("signinStatusData");
    let parsedData = localData ? JSON.parse(localData) : null;
    console.log(
      "[STATIC CRITERIA DEBUG] getData: Retrieved data from localStorage",
      {
        hasSelectedVoen: !!selectedVoen,
        selectedVoen,
        hasLocalData: !!localData,
        hasContainer: !!parsedData?.container,
      }
    );
    if (selectedVoen && localData && parsedData.container) {
      const obj = {
        base64Content: parsedData.container,
        fileType: "adoc",
        voenOrPin: selectedVoen,
      };
      console.log(
        "[STATIC CRITERIA DEBUG] getData: Fetching tax object data..."
      );
      try {
        let taxesObjData = await getTaxesObject(obj);
        console.log(
          "[STATIC CRITERIA DEBUG] getData: Tax object data received",
          {
            hasTourismDTOList: !!taxesObjData?.data?.tourismDTOList,
            tourismListLength: taxesObjData?.data?.tourismDTOList?.length,
          }
        );

        if (taxesObjData?.data?.tourismDTOList) {
          let object = taxesObjData?.data?.tourismDTOList.find(
            (v) => v.address
          );
          console.log(
            "[STATIC CRITERIA DEBUG] getData: Object with address found",
            {
              hasObject: !!object,
              objectAddress: object?.address,
            }
          );
          setTaxPayerObjData(object);
        }
      } catch (error) {
        console.error("[STATIC CRITERIA DEBUG] getData: getTaxesObject error", {
          error: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
      }
      console.log(
        "[STATIC CRITERIA DEBUG] getData: Fetching tax payer info..."
      );
      try {
        setTaxPayerInfoLoading(true);
        let taxesPayerData = await getTaxesPayer(obj);
        console.log(
          "[STATIC CRITERIA DEBUG] getData: Tax payer info received",
          {
            hasLegalAddressData: !!taxesPayerData?.data?.legalPayerEnt
              ?.legalAddressRelationEnt?.addressEnt,
            house:
              taxesPayerData?.data?.legalPayerEnt?.legalAddressRelationEnt
                ?.addressEnt?.house,
          }
        );

        if (
          taxesPayerData?.data?.legalPayerEnt?.legalAddressRelationEnt
            ?.addressEnt
        ) {
          formik.setFieldValue(
            "legalAddress",

            taxesPayerData.data?.legalPayerEnt?.legalAddressRelationEnt
              ?.addressEnt?.house
          );
          console.log(
            "[STATIC CRITERIA DEBUG] getData: Legal address set in formik"
          );
        }
        setTaxPayerInfoLoading(false);
      } catch (error) {
        setTaxPayerInfoLoading(false);
        console.error("[STATIC CRITERIA DEBUG] getData: getTaxesPayer error", {
          error: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
        });
      }
    } else {
      console.log(
        "[STATIC CRITERIA DEBUG] getData: Missing required data (VOEN required)",
        {
          hasSelectedVoen: !!selectedVoen,
          hasLocalData: !!localData,
          hasContainer: !!parsedData?.container,
        }
      );
    }
  };

  const fetchCities = async () => {
    console.log("[STATIC CRITERIA DEBUG] fetchCities: Starting...");
    try {
      const filters = {
        country: "6576bfca218ca87d4627df1d", // Azerbaijan country ID
        target: "StaticCriteriaQuestions",
      };
      const response = await getCities(filters);
      const cityList = response?.data?.list || city;
      console.log("[STATIC CRITERIA DEBUG] fetchCities: Cities fetched", {
        citiesCount: cityList?.length,
      });
      setCities(cityList);
    } catch (error) {
      console.error("[STATIC CRITERIA DEBUG] fetchCities: Error occurred", {
        error: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      setCities(city);
    }
  };

  const handleClickPopover = (event) => {
    setAnchorElPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorElPopover(null);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  function setPhotoPriority(index, e) {
    if (e.target.tagName === "IMG") {
      let toIndex = 0;
      let element = formik?.values?.photo[index];
      let g = formik?.values?.photo.splice(index, 1);
      let g1 = formik?.values?.photo.splice(toIndex, 0, element);
      setFormikPhoto(g1);
      return formik.values?.photo;
    }
  }

  const handleDelete = (name, index) => {
    console.log("[STATIC CRITERIA DEBUG] handleDelete: Deleting item", {
      fieldName: name,
      index,
      currentLength: formik.values[name]?.length,
    });
    let getFieldData = formik.values[name];
    getFieldData = [...getFieldData];
    getFieldData.splice(index, 1);
    formik.setFieldValue(name, getFieldData);
    console.log("[STATIC CRITERIA DEBUG] handleDelete: Item deleted", {
      newLength: getFieldData.length,
    });
  };

  if (formik.values?.photo && typeof formik.values.photo === "object") {
    Object.keys(formik.values.photo).forEach(function eachKey(key) {
      newPhoto[key] = formik.values.photo[key];
    });
  }

  return (
    <div className="card card-custom card-stretch gutter-b">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
      >
        <div className="card-header border-0">
          <div className="d-flex align-items-center w-100">
            <img src={bg} className={classMaterial.logo} />
          </div>
        </div>

        <div className="d-flex gutter-b justify-content-end font-weight-normal text-muted gutter-b mr-11 ml-11">
          {stakeholder?.industrySubCategory &&
          stakeholder?.industrySubCategory !== null ? (
            <>
              <FormattedMessage id="evaluated_subindustry" />
              {" : "}
              <span
                style={{
                  backgroundColor: "#17c191",
                  padding: "0 5px",
                  border: "1px solid #17c191",
                  borderRadius: 4,
                  margin: "0 5px",
                  color: "white",
                  fontWeight: 400,
                }}
              >
                {stakeholder?.industrySubCategory?.name?.[intl.locale] ||
                  stakeholder?.industrySubCategory?.name?.az ||
                  ""}
              </span>
            </>
          ) : isInstitution ? (
            <div>
              <FormattedMessage id="evaluated_institution" />
              {" : "}
              <span
                style={{
                  backgroundColor: "#17c191",
                  padding: "0 5px",
                  border: "1px solid #17c191",
                  borderRadius: 4,
                  margin: "0 5px",
                  color: "white",
                  fontWeight: 400,
                }}
              >
                {stakeholder.name}
              </span>
            </div>
          ) : (
            <div>
              <FormattedMessage id="evaluated_industry" />
              {" : "}
              <span
                style={{
                  backgroundColor: "#17c191",
                  padding: "0 5px",
                  border: "1px solid #17c191",
                  borderRadius: 4,
                  margin: "0 5px",
                  color: "white",
                  fontWeight: 400,
                }}
              >
                {selectedIndustryCategory?.name?.[intl.locale] ||
                  selectedIndustryCategory?.name?.az ||
                  ""}
              </span>
            </div>
          )}
        </div>
        {taxPayerInfoLoading ? (
          <LinearProgress />
        ) : (
          <div className="card-body pt-5">
            <Grid container spacing={2} justify="center">
              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az !== "Bələdçilər" &&
                !isInstitution && (
                  <Grid item lg={6} style={classes.rowStyles}>
                    <TextInput
                      label={intl.formatMessage({
                        id: "stakeholder.objectName",
                      })}
                      name="objectName"
                      value={formik?.values?.objectName}
                      disabled
                    />
                  </Grid>
                )}

              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az !== "Bələdçilər" &&
                !isInstitution && (
                  <Grid item lg={6} style={classes.rowStyles}>
                    {/* <TextField
                                                    id="outlined-basic"
                                                    label="Obyektin kodu*"
                                                    size="small"
                                                    variant="outlined"
                                                    name="objectCode"
                                                    className={classMaterial.inputStyle}
                                                    disabled
                                                    value={formik.values?.objectCode}
                                                    /> */}
                    <TextInput
                      label={intl.formatMessage({
                        id: "staticReg_objectCode",
                      })}
                      name="objectCode"
                      value={formik.values?.objectCode}
                      disabled
                    />
                  </Grid>
                )}

              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az !== "Bələdçilər" &&
                !isInstitution && (
                  <Grid item lg={6} style={classes.rowStyles}>
                    {/* <TextField
                                                    type="text"
                                                    size="small"
                                                    variant="outlined"
                                                    id="outlined-basic"
                                                    label={intl.formatMessage({ id: "staticReg_headPerson" })}
                                                    name="managerName"
                                                    value={formik.values?.managerName}
                                                    onBlur={formik.handleBlur}
                                                    onChange={formik.handleChange}
                                                    className={classMaterial.inputStyle}
                                                    error={Boolean(
                                                        formik.touched?.managerName &&
                                                            formik.errors?.managerName
                                                    )}
                                                    helperText={
                                                        formik.touched?.managerName &&
                                                        formik.errors?.managerName
                                                    }
                                                /> */}
                    <TextInput
                      label={intl.formatMessage({
                        id: "staticReg_headPerson",
                      })}
                      isRequired
                      name="managerName"
                      value={formik.values?.managerName}
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      helperText={
                        formik.touched?.managerName &&
                        formik.errors?.managerName
                      }
                    />
                  </Grid>
                )}

              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az !== "Bələdçilər" &&
                !isInstitution && (
                  <Grid item lg={6} style={classes.rowStyles}>
                    <TextInput
                      label={
                        stakeholder?.voen?.charAt(
                          stakeholder?.voen.length - 1
                        ) === "1"
                          ? `${intl.formatMessage({
                              id: "stakeholder.legalEntityAddress",
                            })}`
                          : `${intl.formatMessage({
                              id: "stakeholder.legalAddress",
                            })}`
                      }
                      isRequired
                      name="legalAddress"
                      value={formik.values?.legalAddress}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                )}

              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az !== "Bələdçilər" &&
                !isInstitution && (
                  <Grid item lg={6} style={classes.rowStyles}>
                    <TextInput
                      label={intl.formatMessage({
                        id: "stakeholder.brandName",
                      })}
                      name="brandName"
                      value={formik.values.brandName}
                      onChange={formik.handleChange}
                      helperText={
                        formik.touched?.brandName && formik.errors?.brandName
                      }
                    />
                  </Grid>
                )}

              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az !== "Bələdçilər" && (
                  <Grid item lg={6} style={classes.rowStyles}>
                    <DatePicker
                      isRequired
                      name="objectRegisterationDate"
                      value={formik.values?.objectRegisterationDate}
                      onBlur={() => {
                        formik.handleBlur({
                          target: { name: "objectRegisterationDate" },
                        });
                      }}
                      onChange={(e) => {
                        formik.setFieldValue(
                          "objectRegisterationDate",
                          e.target.value
                        );
                      }}
                      helperText={
                        formik.touched?.objectRegisterationDate &&
                        formik.errors?.objectRegisterationDate
                      }
                      label={intl.formatMessage({
                        id: "stakeholder.objectRegistrationDate",
                      })}
                    />
                  </Grid>
                )}

              {!isInstitution && (
                <Grid item lg={6} style={classes.rowStyles}>
                  <CustomPhoneInput
                    name="phone"
                    isRequired
                    value={formik.values?.phone}
                    label={
                      selectedIndustryCategory?.name?.az === "Bələdçilər" &&
                      !isInstitution
                        ? intl.formatMessage({ id: "staticReg_phone" })
                        : intl.formatMessage({
                            id: "staticReg_objectPhoneNumber",
                          })
                    }
                    onChange={(e) => {
                      formik.setFieldValue("phone", e);
                    }}
                    helperText={formik.touched?.phone && formik.errors?.phone}
                  />
                </Grid>
              )}

              {!isInstitution && (
                <Grid item lg={6} style={classes.rowStyles}>
                  <CustomSelect
                    isRequired
                    optionValues={(cities || [])
                      .filter((city) => city?.name)
                      .sort((a, b) =>
                        (a.name?.[intl.locale] || "").localeCompare(
                          b.name?.[intl.locale] || ""
                        )
                      )
                      .map((city) => city.name?.az || city._id)}
                    optionLabels={(cities || [])
                      .filter((city) => city?.name)
                      .sort((a, b) =>
                        (a.name?.[intl.locale] || "").localeCompare(
                          b.name?.[intl.locale] || ""
                        )
                      )
                      .map((city) => city.name?.az || "")}
                    placeholder={
                      selectedIndustryCategory?.name?.az ===
                      "Yerləşmə vasitələri"
                        ? intl.formatMessage({ id: "staticReg_objectCity" })
                        : intl.formatMessage({ id: "staticReg_city" })
                    }
                    label={
                      selectedIndustryCategory?.name?.az ===
                      "Yerləşmə vasitələri"
                        ? intl.formatMessage({ id: "staticReg_objectCity" })
                        : intl.formatMessage({ id: "staticReg_city" })
                    }
                    name="city"
                    value={formik?.values?.city}
                    onChange={(value) => formik.setFieldValue("city", value)}
                    helperText={formik.errors?.city && formik.errors?.city}
                  />
                </Grid>
              )}

              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az ===
                  "Yerləşmə vasitələri" && (
                  <Grid item lg={6} style={classes.rowStyles}>
                    <TextInput
                      label={`${intl.formatMessage({
                        id: "stakeholder.bedCount",
                      })}`}
                      isRequired
                      value={formik.values?.bedCount}
                      name="bedCount"
                      onChange={(e) =>
                        formik.setFieldValue(
                          "bedCount",
                          e.target.value
                            .replace(/[^0-9.]/g, "")
                            .replace(/(\..*?)\..*/g, "$1")
                        )
                      }
                      helperText={
                        formik.touched?.bedCount && formik.errors?.bedCount
                      }
                    />
                  </Grid>
                )}

              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az ===
                  "Yerləşmə vasitələri" && (
                  <Grid item lg={6} style={classes.rowStyles}>
                    <TextInput
                      label={`${intl.formatMessage({
                        id: "stakeholder.roomCount",
                      })}`}
                      isRequired
                      value={formik.values?.roomCount}
                      name="roomCount"
                      onChange={(e) =>
                        formik.setFieldValue(
                          "roomCount",
                          e.target.value
                            .replace(/[^0-9.]/g, "")
                            .replace(/(\..*?)\..*/g, "$1")
                        )
                      }
                      helperText={
                        formik.touched?.roomCount && formik.errors?.roomCount
                      }
                    />
                  </Grid>
                )}

              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az !== "Bələdçilər" &&
                !isInstitution &&
                stakeholder?.voen?.charAt(stakeholder?.voen.length - 1) ===
                  "1" && (
                  <Grid item lg={6} style={classes.rowStyles}>
                    <TextInput
                      label={intl.formatMessage({
                        id: "stakeholder.companyGroup",
                      })}
                      name="groupOfCompanies"
                      value={formik.values?.groupOfCompanies}
                      onBlur={formik.handleBlur}
                      onChange={(e) =>
                        formik.setFieldValue("groupOfCompanies", e.target.value)
                      }
                      helperText={
                        formik.touched?.groupOfCompanies &&
                        formik.errors?.groupOfCompanies
                      }
                    />
                  </Grid>
                )}

              {(isInstitution ||
                selectedIndustryCategory?.name?.az === "Bələdçilər") && (
                <Grid item lg={6} style={classes.rowStyles}>
                  {/* <TextField
                                        id="outlined-basic"
                                        label={`${intl.formatMessage({
                                            id: "reg_input_nationality",
                                        })}*`}
                                        size="small"
                                        variant="outlined"
                                        name="nationality"
                                        className={classMaterial.inputStyle}
                                        value={formik.values?.nationality}
                                        onBlur={formik.handleBlur}
                                        onChange={formik.handleChange}
                                        error={Boolean(
                                            formik.touched?.nationality && formik.errors?.nationality
                                        )}
                                        helperText={formik.touched?.nationality && formik.errors?.nationality}
                                    /> */}
                  <TextInput
                    name="nationality"
                    label={intl.formatMessage({
                      id: "reg_input_nationality",
                    })}
                    isRequired
                    value={formik.values?.nationality}
                    onChange={formik.handleChange}
                    helperText={
                      formik.touched?.nationality && formik.errors?.nationality
                    }
                  />
                </Grid>
              )}

              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az !== "Bələdçilər" && (
                  <Grid item lg={6} style={classes.rowStyles}>
                    <TextInput
                      label={intl.formatMessage({
                        id: "staticReg_authorizedPersonName",
                      })}
                      isRequired
                      name="personInChargeName"
                      value={formik.values?.personInChargeName}
                      onChange={formik.handleChange}
                    />
                  </Grid>
                )}

              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az !== "Bələdçilər" && (
                  <Grid
                    className="justify-content-end"
                    item
                    lg={6}
                    style={classes.rowStyles}
                  >
                    <TextInput
                      name="personInChargePosition"
                      isRequired
                      label={`${intl.formatMessage({
                        id: "stakeholder.personInChargePosition",
                      })}`}
                      value={formik?.values?.personInChargePosition?.az || ""}
                      onChange={(e) => {
                        formik.setFieldValue("personInChargePosition", {
                          az: e.target.value,
                        });
                      }}
                      helperText={
                        formik.touched?.personInChargePosition &&
                        formik?.errors?.personInChargePosition?.az
                      }
                    />
                  </Grid>
                )}

              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az !== "Bələdçilər" && (
                  <Grid item lg={6} style={classes.rowStyles}>
                    <CustomPhoneInput
                      isRequired
                      name="personInChargePhone"
                      label={intl.formatMessage({
                        id: "staticReg_authPersonContact",
                      })}
                      value={formik.values?.personInChargePhone}
                      onChange={(value) => {
                        formik.setFieldValue("personInChargePhone", value);
                      }}
                      helperText={
                        formik.touched?.personInChargePhone &&
                        formik.errors?.personInChargePhone
                      }
                    />
                  </Grid>
                )}

              <Grid
                item
                lg={6}
                style={{ ...classes.rowStyles, marginTop: "2px" }}
              >
                <TextInput
                  type="email"
                  label={intl.formatMessage({ id: "staticReg_objectEmail" })}
                  isRequired
                  name="email"
                  value={formik.values?.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  helperText={formik.touched?.email && formik.errors?.email}
                  // style={
                  //     selectedIndustryCategory?.name?.az === "Yerləşmə vasitələri"
                  //         ? {
                  //             transform: "translateY(25px)",
                  //         }
                  //         : { transform: "translateY(-2px)" }
                  // }
                />
              </Grid>

              {selectedIndustryCategory?.name?.az !== "Yerləşmə vasitələri" &&
                !isInstitution && (
                  <Grid item lg={6} style={classes.rowStyles}>
                    <TextInput
                      label={intl.formatMessage({
                        id: "stakeholder.name_name_name",
                      })}
                      name="name"
                      value={formik.values?.name}
                      disabled
                    />
                  </Grid>
                )}

              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az !== "Bələdçilər" &&
                !isInstitution && (
                  <Grid item lg={6} style={classes.rowStyles}>
                    {/* <TextField
                                                type="text"
                                                size="small"
                                                variant="outlined"
                                                id="outlined-basic"
                                                label="Web səhifə"
                                                name="website"
                                                value={formik.values?.website}
                                                onBlur={formik.handleBlur}
                                                onChange={(e) =>
                                                    formik.setFieldValue("website", e.target.value)
                                                }
                                                className={classMaterial.inputStyle}
                                                error={Boolean(
                                                    formik.touched?.website && formik.errors?.website
                                                )}
                                                helperText={formik.touched?.website && formik.errors?.website}
                                            /> */}
                    <TextInput
                      name="website"
                      label={intl.formatMessage({ id: "staticReg_webpage" })}
                      value={formik.values?.website}
                      onChange={(e) =>
                        formik.setFieldValue("website", e.target.value)
                      }
                      helperText={
                        formik.touched?.website && formik.errors?.website
                      }
                    />
                  </Grid>
                )}

              {!isInstitution && (
                <Grid item lg={6} style={classes.rowStyles}>
                  <div className={classMaterial.inputStyle}>
                    <div
                      className={classMaterial.label}
                      style={
                        formik.errors?.photo
                          ? { color: "#f018a6" }
                          : { color: "#6b7280" }
                      }
                    ></div>
                    <label
                      htmlFor="photo"
                      style={{
                        ...customStyles.display,
                        display: "block",
                        width: "100%",
                        height: "52px",
                        border: "1px solid #d1d5db",
                        borderRadius: "4px",
                        padding: "0px 10px",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        {/* <span style={classes.btnUploaded}>
                                                        <FormattedMessage id="upload" />
                                                    </span> */}
                        <span style={classes.fileUpload}>
                          <span>
                            <FormattedMessage id="staticReg_logo" />
                            <span style={{ color: "red" }}>*</span>
                          </span>
                          <br />
                          <a href="#" onClick={(e) => e.preventDefault()}>
                            {intl.formatMessage({ id: "click-to-download" })}
                          </a>
                        </span>
                        <Tooltip title="Şəkil haqqında məlumat">
                          <IconButton
                            aria-describedby={idPopover}
                            onClick={handleClickPopover}
                            style={{ width: "4rem" }}
                          >
                            <InfoOutlined style={{ fontSize: "2rem" }} />
                          </IconButton>
                        </Tooltip>
                        <Popover
                          id={idPopover}
                          open={openPopover}
                          anchorEl={anchorElPopover}
                          onClose={handleClosePopover}
                          anchorOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                        >
                          <Typography
                            style={{
                              whiteSpace: "pre-line",
                              padding: "1rem",
                            }}
                          >
                            {textPopover}
                          </Typography>
                        </Popover>
                        <input
                          type="file"
                          multiple
                          name="photo"
                          id="photo"
                          min={1}
                          onBlur={formik.handleBlur}
                          accept="image/png, image/jpeg, image/jpg, application/pdf"
                          onChange={(e) => {
                            const files = Array.from(e.target.files);
                            formik.setFieldValue("photo", [
                              ...formik.values.photo,
                              ...files,
                            ]);
                          }}
                          className="opacity-0 d-none"
                        />
                      </div>
                    </label>
                    {formik.errors?.photo && (
                      <p style={{ color: "red" }}>{formik.errors?.photo}</p>
                    )}
                    <div
                      style={{
                        ...registrationStyle.thumbnail_box,
                        display: "flex",
                        gap: "20px",
                        rowGap: "20px",
                      }}
                    >
                      {formik.values?.photo?.length > 0
                        ? formik.values?.photo?.map((item, index) => (
                            <div
                              style={registrationStyle.thumbnail}
                              key={index}
                              onClick={(e) => setPhotoPriority(index, e)}
                              className="image-container"
                            >
                              {item?.url ? (
                                <img
                                  alt={item?.url}
                                  // src={`/proxy/public/stakeholder/getFile/${item?.url}`}
                                  src={getFile.getFile(item?.url)}
                                  style={registrationStyle.imgPhoto}
                                />
                              ) : (
                                <img
                                  src={
                                    item?.base64
                                      ? item?.base64
                                      : URL.createObjectURL(item)
                                  }
                                  alt={item?.name}
                                  style={registrationStyle.imgPhoto}
                                />
                              )}
                              <div className={classMaterial.uploadImage}>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="30"
                                  height="30"
                                  onClick={() => handleDelete("photo", index)}
                                  fill="currentColor"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z" />
                                </svg>
                              </div>
                            </div>
                          ))
                        : null}
                    </div>
                    <span>
                      <FormattedMessage id="main.photo" />
                    </span>
                  </div>
                </Grid>
              )}

              {stakeholder?.voen &&
                selectedIndustryCategory?.name?.az !== "Bələdçilər" &&
                !isInstitution && (
                  <Grid
                    item
                    lg={6}
                    style={{
                      ...classes.rowStyles,
                      paddingTop: "10px",
                      position: "relative",
                    }}
                  >
                    <AddressInput
                      style={{ backgroundColor: "red" }}
                      onBlur={formik.onBlur}
                      label={
                        intl.formatMessage({
                          id: "stakeholder.objectAdress",
                        }) + " *"
                      }
                      data={formik.values.objectAddress || {}}
                      onChange={(v) => {
                        setGoogleMapAddress(v);
                        formik.setFieldValue("objectAddress", v);
                      }}
                      fullWidth={true}
                    />
                    {formik.errors?.objectAddress && (
                      <p style={{ color: "red" }}>
                        {formik.errors?.objectAddress?.label}
                      </p>
                    )}
                  </Grid>
                )}
              <div
                className="d-flex justify-content-center"
                style={{ marginTop: "55px", width: "100%" }}
              >
                <iframe
                  width="560"
                  height="315"
                  src="https://www.youtube.com/embed/izYTVYXicZc"
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
              <div
                className="card-toolbar d-flex justify-content-between mt-8"
                style={classes.vw_100}
              >
                <MuiThemeProvider theme={theme}>
                  <Button
                    style={{ textTransform: "capitalize" }}
                    variant="outlined"
                    color="default"
                    size="small"
                    onClick={() =>
                      stakeholder?.active
                        ? history.goBack()
                        : setRegistrationStep(STEPS.INDUSTRY_CHOICE)
                    }
                    startIcon={<ArrowBack />}
                  >
                    <FormattedMessage id="button.back" />
                  </Button>
                  <Button
                    type="submit"
                    style={{
                      backgroundColor: "rgb(100, 38, 187)",
                      textTransform: "capitalize",
                      width: "160px",
                      color: "#fff",
                      height: "45px",
                      marginLeft: "10px",
                    }}
                    variant="contained"
                    color="secondary"
                    size="small"
                    // disabled={true}
                    endIcon={<DoubleArrow />}
                  >
                    <FormattedMessage id="button.submit" />
                  </Button>
                </MuiThemeProvider>
              </div>
            </Grid>
          </div>
        )}
      </form>
    </div>
  );
};

// Wrapper component with ErrorBoundary for graceful error handling
const StaticCriteriaQuestionsWithErrorBoundary = (props) => (
  <ErrorBoundary>
    <StaticCriteriaQuestions {...props} />
  </ErrorBoundary>
);

export default StaticCriteriaQuestionsWithErrorBoundary;
